// src/pages/SizingCostCombined.tsx
// Combined Sizing & Cost Simulator page for direct sharing with users

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Server, DollarSign, ChevronDown, FileDown, Loader2, Cpu, TrendingUp, Calculator, Sparkles } from 'lucide-react';
import { QuickControlsCollapsible } from '@/components/sizing/QuickControlsCollapsible';
import { SizingSummaryDashboard } from '@/components/sizing/SizingSummaryDashboard';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import ErrorPanel from '@/components/outputs/ErrorPanel';
import { NarrativePanels } from '@/components/outputs/NarrativePanels';
import { CostSimulatorTool } from '@/components/cost-simulator/CostSimulatorTool';

import { useCatalog } from '@/hooks/useCatalog';
import { usePersonaCatalog, usePersonaProfiles } from '@/hooks/usePersonaCatalog';
import { useSizingEngineV2 } from '@/hooks/useSizingEngineV2';
import { useCostSimulatorSizing } from '@/hooks/useCostSimulatorSizing';
import { useCostSimulatorState } from '@/hooks/useCostSimulatorState';
import { buildMcRequest } from '@/sizing/buildMcRequest';
import { useNewSizingNarrative } from '@/hooks/useNewSizingNarrative';
import type { NewSizingRequest } from '@/sizing/newSizingEngine';
import { SCENARIO_PRESETS } from '@/hooks/useSizingStateV2';
import { resolveEffectivePersona } from '@/lib/personaResolver';
import type { PersonaProfile } from '@/types/personas';
import { Button } from '@/components/ui/button';
import { generateTextBasedPdf, type PdfReportData } from '@/lib/pdfReportBuilder';
import { generateMonthlyRollups, findBreakEvenMonth, DEFAULT_COST_CONFIG } from '@/lib/costCalculations';
import { toast } from '@/hooks/use-toast';
const BYTES_PER_GIB = 1024 ** 3;

// Introduction Hero Section
const IntroSection = ({
  onScrollToSizing,
  onScrollToCost
}: {
  onScrollToSizing: () => void;
  onScrollToCost: () => void;
}) => <section className="relative overflow-hidden py-16 lg:py-24">
    {/* Background effects */}
    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
    
    <div className="relative container mx-auto px-4">
      <div className="max-w-4xl mx-auto text-center space-y-8">
        {/* Badge */}
        
        
        {/* Main Headline */}
        <h1 className="text-4xl lg:text-6xl font-bold tracking-tight">
          <span className="bg-gradient-to-r from-foreground via-foreground to-foreground/80 bg-clip-text text-transparent">
            Plan & Optimize Your
          </span>
          <br />
          <span className="bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent">
            On-Prem GenAI Investment
          </span>
        </h1>
        
        {/* Subheadline */}
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Size your on-premises inference infrastructure and compare total cost of ownership 
          against cloud alternatives—all in one comprehensive analysis.
        </p>
        
        {/* Value Prop Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto pt-4">
          {[{
          icon: Server,
          label: 'Server Sizing',
          desc: 'GPU fleet planning'
        }, {
          icon: Cpu,
          label: 'Memory Analysis',
          desc: 'KV cache optimization'
        }, {
          icon: Calculator,
          label: 'TCO Analysis',
          desc: '5-year projections'
        }, {
          icon: TrendingUp,
          label: 'Break-even',
          desc: 'ROI timeline'
        }].map(({
          icon: Icon,
          label,
          desc
        }) => <div key={label} className="p-4 rounded-xl bg-card/50 border border-border/50 backdrop-blur">
              <Icon className="h-6 w-6 text-primary mx-auto mb-2" />
              <div className="text-sm font-semibold text-foreground">{label}</div>
              <div className="text-xs text-muted-foreground">{desc}</div>
            </div>)}
        </div>
        
        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <Button size="lg" onClick={onScrollToSizing} className="bg-gradient-to-r from-primary to-primary/90 hover:shadow-lg transition-all gap-2">
            <Server className="h-5 w-5" />
            Plan Infrastructure
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button size="lg" variant="outline" onClick={onScrollToCost} className="gap-2">
            <DollarSign className="h-5 w-5" />
            Analyze Costs
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  </section>;

// Section Divider
const SectionDivider = ({
  title,
  icon: Icon,
  id
}: {
  title: string;
  icon: React.ElementType;
  id?: string;
}) => <div id={id} className="py-8 border-y bg-muted/30 scroll-mt-16">
    <div className="container flex items-center gap-3 justify-center">
      <Icon className="h-6 w-6 text-primary" />
      <span className="text-xl font-semibold text-foreground">{title}</span>
    </div>
  </div>;
const SizingCostCombined = () => {
  // Refs for scrolling
  const sizingSectionRef = useRef<HTMLDivElement>(null);
  const costSectionRef = useRef<HTMLDivElement>(null);

  // Catalog and persona data
  const {
    catalog,
    isLoading: catalogLoading,
    error,
    catalogCommitted,
    catalogHash
  } = useCatalog();
  const catalogReady = catalog?.ready ?? false;
  const personaProfilesQuery = usePersonaProfiles();
  const personaProfiles = personaProfilesQuery.data ?? [];
  const sizing = useSizingEngineV2({
    personaProfiles: personaProfilesQuery.data ?? []
  });
  const personaCatalog = usePersonaCatalog({
    enabled: !catalogLoading && !error
  });
  const {
    snapshot,
    compute,
    loading
  } = sizing;

  // Cost simulator state
  const costSizing = useCostSimulatorSizing();
  const costSnapshot = costSizing.snapshot;
  const {
    comparison
  } = useCostSimulatorState(costSnapshot);
  

  // PDF export state
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Helper functions
  const toNum = (value: unknown): number | null => {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const parseJson = <T,>(value: unknown): T | null => {
    if (!value) return null;
    try {
      return typeof value === 'string' ? JSON.parse(value) as T : value as T;
    } catch {
      return null;
    }
  };
  const resolvePersonaLatency = (profile: PersonaProfile | undefined, mode: 'snappy' | 'fast' | 'resp' | 'comfy' | 'async' | 'responsive') => {
    if (!profile) {
      return {
        ttftSec: 0,
        decodeTokPerSec: 0
      };
    }
    const key = mode === 'responsive' ? 'resp' : mode;
    const ttftMsByKey: Record<'snappy' | 'fast' | 'resp' | 'comfy' | 'async', number | null> = {
      snappy: toNum((profile as any).ttft_snappy_ms),
      fast: toNum((profile as any).ttft_fast_ms),
      resp: toNum((profile as any).ttft_resp_ms),
      comfy: toNum((profile as any).ttft_comfy_ms),
      async: toNum((profile as any).ttft_async_ms)
    };
    const decodeTpsByKey: Record<'snappy' | 'fast' | 'resp' | 'comfy' | 'async', number | null> = {
      snappy: toNum((profile as any).decode_tps_snappy),
      fast: toNum((profile as any).decode_tps_fast),
      resp: toNum((profile as any).decode_tps_resp),
      comfy: toNum((profile as any).decode_tps_comfy),
      async: toNum((profile as any).decode_tps_async)
    };
    const ttftMs = ttftMsByKey[key] ?? toNum(profile.default_latency_slo_ms) ?? 0;
    const decodeTokPerSec = decodeTpsByKey[key] ?? 0;
    const ttftSec = ttftMs / 1000;
    return {
      ttftSec,
      decodeTokPerSec
    };
  };

  // Latency mode derivation
  const tolerance = sizing.scenario.tradeoffs.latencyTolerance ?? 'balanced';
  const slaMode = (() => {
    switch (tolerance) {
      case 'aggressive':
        return 'snappy' as const;
      case 'fast':
        return 'fast' as const;
      case 'balanced':
        return 'responsive' as const;
      case 'relaxed':
        return 'comfy' as const;
      case 'queue':
      case 'none':
      default:
        return 'async' as const;
    }
  })();
  const defaultBatchForTolerance: Record<string, number> = {
    aggressive: 1,
    fast: 4,
    balanced: 16,
    relaxed: 64,
    queue: 256,
    none: 256
  };
  const effectiveBatch = defaultBatchForTolerance[tolerance] ?? defaultBatchForTolerance.balanced;
  const mcLatencyMode = slaMode === 'responsive' ? 'resp' : slaMode;

  // Throughput entry
  const throughputEntry = useMemo(() => {
    if (!catalogCommitted || !catalogReady) return null;
    if (!catalog?.throughput?.length) return null;
    const modelId = sizing.scenario.selectedModelId ?? catalog.models?.[0]?.model_id;
    if (!modelId) return null;
    const precisionBits = Number(sizing.scenario.tradeoffs.precision ?? 8);
    const backend = catalog.accelerator?.backend ?? null;
    const batchTarget = effectiveBatch;
    const throughputPoints = catalog.throughput;
    const primaryCandidates = throughputPoints.filter(point => {
      if (point.model_id !== modelId) return false;
      if (Number(point.precision) !== precisionBits) return false;
      if (backend && (point.backend ?? backend) !== backend) return false;
      return true;
    });
    let chosen = null as (typeof throughputPoints)[number] | null;
    if (primaryCandidates.length > 0) {
      const sorted = [...primaryCandidates].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
      chosen = sorted.find(p => (p.batch ?? 0) >= batchTarget) ?? sorted[sorted.length - 1];
    }
    if (!chosen) {
      const modelBackendCandidates = throughputPoints.filter(point => {
        if (point.model_id !== modelId) return false;
        if (backend && (point.backend ?? backend) !== backend) return false;
        return true;
      });
      if (modelBackendCandidates.length > 0) {
        const sorted = [...modelBackendCandidates].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
        chosen = sorted.find(p => (p.batch ?? 0) >= batchTarget) ?? sorted[sorted.length - 1];
      }
    }
    if (!chosen) {
      const anyModel = throughputPoints.filter(point => point.model_id === modelId);
      if (anyModel.length > 0) {
        const sorted = [...anyModel].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
        chosen = sorted.find(p => (p.batch ?? 0) >= batchTarget) ?? sorted[sorted.length - 1];
      }
    }
    chosen = chosen ?? throughputPoints[0] ?? null;
    return chosen;
  }, [catalog, catalogCommitted, catalogReady, sizing.scenario.selectedModelId, effectiveBatch, sizing.scenario.tradeoffs.precision]);

  // Build sizing request
  const usersCount = sizing.scenario.users.length;
  const personaCatalogReady = !personaCatalog.isLoading && !!personaCatalog.data;
  const profilesReady = !personaProfilesQuery.isLoading && !!personaProfilesQuery.data;
  const sizingRequest = useMemo<NewSizingRequest | null>(() => {
    if (catalogLoading) return null;
    if (!catalogReady || !catalogCommitted) return null;
    if (!catalog || !personaCatalogReady || !throughputEntry || !profilesReady) return null;
    const concurrentUsers = sizing.scenario.tradeoffs.concurrentUsers ?? 0;
    if (concurrentUsers <= 0) return null;
    if (usersCount <= 0) return null;
    const profilesById: Record<string, PersonaProfile> = Object.fromEntries(personaProfiles.map(p => [p.id, p]));
    const scenarioUsers = sizing.scenario.users;
    const personaCount = scenarioUsers.length || 1;
    const turns = sizing.scenario.tradeoffs.turns ?? 1;
    const effectivePersonas = scenarioUsers.map(user => resolveEffectivePersona(user, profilesById, slaMode, personaCount));
    const personas = effectivePersonas.map((persona, idx) => {
      const user = scenarioUsers[idx];
      const profile = user?.profileId && user.profileId !== 'custom' ? profilesById[user.profileId] : undefined;
      const population = concurrentUsers * (persona.sharePercent / 100);
      const personaLatencyMode = persona.latencyMode ?? slaMode;
      const latencyKey = personaLatencyMode === 'responsive' ? 'resp' : personaLatencyMode;
      const effectiveLatencyMs = profile ? (() => {
        switch (latencyKey) {
          case 'snappy':
            return toNum((profile as any).e2e_target_snappy_ms);
          case 'fast':
            return toNum((profile as any).e2e_target_fast_ms);
          case 'resp':
            return toNum((profile as any).e2e_target_resp_ms);
          case 'comfy':
            return toNum((profile as any).e2e_target_comfy_ms);
          case 'async':
            return toNum((profile as any).e2e_target_async_ms);
          default:
            return null;
        }
      })() : null;
      const inputTokensPerConversation = persona.inputTokensPerTurn * turns;
      const outputTokensPerConversation = persona.outputTokensPerTurn * turns;
      const {
        ttftSec,
        decodeTokPerSec
      } = resolvePersonaLatency(profile, latencyKey);
      const streamSec = decodeTokPerSec > 0 ? outputTokensPerConversation / decodeTokPerSec : 0;
      const digestSec = ttftSec + streamSec;
      const latency_profiles = profile ? {
        snappy: {
          ttft_ms: toNum((profile as any).ttft_snappy_ms) ?? 0,
          decode_tps: toNum((profile as any).decode_tps_snappy) ?? 0
        },
        fast: {
          ttft_ms: toNum((profile as any).ttft_fast_ms) ?? 0,
          decode_tps: toNum((profile as any).decode_tps_fast) ?? 0
        },
        resp: {
          ttft_ms: toNum((profile as any).ttft_resp_ms) ?? 0,
          decode_tps: toNum((profile as any).decode_tps_resp) ?? 0
        },
        comfy: {
          ttft_ms: toNum((profile as any).ttft_comfy_ms) ?? 0,
          decode_tps: toNum((profile as any).decode_tps_comfy) ?? 0
        },
        async: {
          ttft_ms: toNum((profile as any).ttft_async_ms) ?? 0,
          decode_tps: toNum((profile as any).decode_tps_async) ?? 0
        }
      } : undefined;
      return {
        id: persona.id ?? `persona-${idx}`,
        name: persona.display_name ?? `Persona ${idx + 1}`,
        display_name: persona.display_name ?? `Persona ${idx + 1}`,
        prompt_tokens_per_turn: persona.inputTokensPerTurn,
        response_tokens_per_turn: persona.outputTokensPerTurn,
        turns_kept: turns,
        read_sec_default: persona.readSec,
        think_sec_default: persona.thinkSec,
        sharePercent: persona.sharePercent,
        population,
        inputTokensPerConversation,
        outputTokensPerConversation,
        readSeconds: persona.readSec,
        thinkSeconds: persona.thinkSec,
        ttftMs: ttftSec * 1000,
        decodeTps: decodeTokPerSec,
        turnsKept: turns,
        latencyTargetMs: effectiveLatencyMs,
        tokens: {
          input: persona.inputTokensPerTurn,
          output: persona.outputTokensPerTurn
        },
        readSec: persona.readSec,
        thinkSec: persona.thinkSec,
        digestSec,
        turnsPerSession: turns,
        latency_profiles,
        latency_slo_default_ms: profile?.default_latency_slo_ms ?? 0,
        profileId: user?.profileId
      };
    });
    const selectedModelId = sizing.scenario.selectedModelId ?? catalog.models?.[0]?.model_id ?? 'model';
    const modelRow = catalog.models?.find(m => m.model_id === selectedModelId) ?? catalog.models?.find(m => m.model_id === catalog.models?.[0]?.model_id);
    const precisionBits = Number(sizing.scenario.tradeoffs.precision ?? 8);
    let kvBytesPerToken = 0;
    let weightsGiB = 0;
    let weightsGiBUnshared = 0;
    let runtimeOverheadGiB = 0;
    let usableKvGiBPerGpu = 0;
    let hbmGiB = 0;
    let fitsWeightsWithoutSharing = false;
    let fitsWeightsWithSharing = false;
    let bridgeGroupSize = 1;
    let weightsSharedWithinGroup = false;
    if (modelRow && catalog.accelerator) {
      const kvMap = parseJson<Record<string, number>>(modelRow.kv_per_token_json) ?? {};
      const weightsMap = parseJson<Record<string, number>>(modelRow.weights_mem_json) ?? {};
      kvBytesPerToken = kvMap[String(precisionBits)] ?? 0;
      const accel = catalog.accelerator as any;
      bridgeGroupSize = Number(accel.bridge_group_size_max ?? 1) || 1;
      weightsSharedWithinGroup = true;
      const weightsBytesRaw = weightsMap[String(precisionBits)] ?? 0;
      weightsGiBUnshared = weightsBytesRaw / BYTES_PER_GIB;
      const weightsBytesPerGpu = weightsSharedWithinGroup && bridgeGroupSize > 0 ? weightsBytesRaw / bridgeGroupSize : weightsBytesRaw;
      weightsGiB = weightsBytesPerGpu / BYTES_PER_GIB;
      const hbmB = Number(accel.hbm_bytes ?? 0);
      hbmGiB = hbmB / BYTES_PER_GIB;
      const modelRuntimeBytes = Number(modelRow.base_runtime_overhead_bytes ?? 0);
      const reservedRuntimeBytesPerGpu = Number(accel.reserved_runtime_bytes ?? 0);
      const runtimeGroupBytes = Number(accel.runtime_overhead_bytes_per_group ?? 0);
      const runtimeGroupBytesPerGpu = bridgeGroupSize > 0 ? runtimeGroupBytes / bridgeGroupSize : runtimeGroupBytes;
      const totalRuntimeBytesPerGpu = modelRuntimeBytes + reservedRuntimeBytesPerGpu + runtimeGroupBytesPerGpu;
      runtimeOverheadGiB = totalRuntimeBytesPerGpu / BYTES_PER_GIB;
      fitsWeightsWithoutSharing = hbmGiB > 0 ? weightsGiBUnshared + runtimeOverheadGiB <= hbmGiB : false;
      fitsWeightsWithSharing = hbmGiB > 0 ? weightsGiB + runtimeOverheadGiB <= hbmGiB : false;
      usableKvGiBPerGpu = Math.max(0, hbmGiB - weightsGiB - runtimeOverheadGiB);
    }
    const model = {
      id: modelRow?.model_id ?? selectedModelId,
      paramsBillions: Number(modelRow?.params_b ?? 0),
      precisionBits,
      kvBytesPerToken,
      weightsGiB,
      usableKvGiBPerGpu,
      hbmGiB,
      weightsGiBUnshared,
      fitsWeightsWithoutSharing,
      fitsWeightsWithSharing,
      bridgeGroupSize,
      weightsSharedWithinGroup
    };
    const throughput = {
      promptTpsPerGpu: Number((throughputEntry as any).prompt_tps ?? (throughputEntry as any).promptTps ?? 0),
      decodeTpsPerGpu: Number((throughputEntry as any).decode_tps ?? (throughputEntry as any).decodeTps ?? 0)
    };
    const uiState = {
      conversationTurns: sizing.scenario.tradeoffs.turns ?? 3,
      batchSize: effectiveBatch,
      latencySloMs: sizing.scenario.advanced.latencyMs ?? sizing.scenario.advanced.latencyTargetMs ?? 0,
      mcFastTrials: 8,
      mcRefineTrials: 32,
      mcHorizonSeconds: 600,
      trafficVolatility: 0.2
    };
    const req = buildMcRequest({
      personas,
      model,
      throughput,
      uiState,
      scenario: {
        concurrentUsers
      },
      latencyMode: mcLatencyMode
    });
    if (!req) {
      return null;
    }
    return req;
  }, [catalog, catalogHash, catalogCommitted, catalogLoading, catalogReady, personaCatalog.data, personaCatalogReady, throughputEntry, personaProfiles, profilesReady, sizing.scenario.selectedModelId, sizing.scenario.tradeoffs.concurrentUsers, sizing.scenario.tradeoffs.turns, sizing.scenario.tradeoffs.latencyTolerance, sizing.scenario.advanced.latencyMs, sizing.scenario.advanced.latencyTargetMs, sizing.scenario.users, usersCount, effectiveBatch, slaMode, mcLatencyMode]);

  // Trigger sizing computation
  useEffect(() => {
    if (!sizingRequest) return;
    compute(sizingRequest);
  }, [compute, sizingRequest]);

  // Narrative data for PDF
  const narrativeData = useNewSizingNarrative({
    snapshot,
    modelName: sizingRequest?.model.id,
    serverSku: 'XE7740',
    serverDisplayName: 'Dell XE7740',
    gpusPerServer: 8,
    hbmBytesPerGpu: (sizingRequest?.model.hbmGiB ?? 96) * BYTES_PER_GIB,
    weightsGiB: sizingRequest?.model.weightsGiB,
    weightsGiBUnshared: sizingRequest?.model.weightsGiBUnshared ?? sizingRequest?.model.weightsGiB,
    runtimeOverheadGiB: sizingRequest ? (sizingRequest.model.hbmGiB ?? 96) - sizingRequest.model.weightsGiB - sizingRequest.model.usableKvGiBPerGpu : undefined,
    usableKvGiBPerGpu: sizingRequest?.model.usableKvGiBPerGpu,
    bridgeGroupSize: sizingRequest?.model.bridgeGroupSize ?? 1,
    conversationTurns: sizingRequest?.conversationTurns,
    latencyMs: sizingRequest?.latencySloMs,
    batchSize: sizingRequest?.batchSize,
    baseConcurrentUsers: sizingRequest?.baseConcurrentUsers,
    personas: sizingRequest?.personas
  });

  // Scroll handlers
  const scrollToSizing = useCallback(() => {
    sizingSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);
  const scrollToCost = useCallback(() => {
    costSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);

  // PDF Export handler
  const handleExportPdf = useCallback(async () => {
    if (!snapshot || !sizingRequest) {
      toast({
        title: 'Unable to Export',
        description: 'Please wait for sizing results to load.',
        variant: 'destructive'
      });
      return;
    }
    setIsExportingPdf(true);
    try {
      // Build cost data
      const costConfig = { ...DEFAULT_COST_CONFIG };
      const monthlyRollups = generateMonthlyRollups(snapshot, costConfig);
      const costComparison = findBreakEvenMonth(monthlyRollups);
      
      const pdfData: PdfReportData = {
        snapshot,
        request: sizingRequest,
        narrativeData,
        serverDisplayName: catalog?.server?.display_name ?? 'Dell XE7740',
        acceleratorDisplayName: catalog?.accelerator?.display_name ?? 'Intel Gaudi 3',
        costData: {
          config: costConfig,
          comparison: costComparison,
          monthlyRollups,
        },
      };
      
      await generateTextBasedPdf(pdfData);
      
      toast({
        title: 'Report Exported',
        description: 'Your sizing report has been downloaded as PDF.'
      });
    } catch (err) {
      console.error('[SizingCostCombined] PDF export error:', err);
      toast({
        title: 'Export Failed',
        description: 'Unable to generate PDF. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsExportingPdf(false);
    }
  }, [snapshot, sizingRequest, narrativeData, catalog]);

  // Loading state
  if (catalogLoading || personaCatalog.isLoading || personaProfilesQuery.isLoading) {
    return <LoadingScreen message="Loading catalog..." />;
  }

  // Error state
  if (error || personaCatalog.error || personaProfilesQuery.error) {
    const err = error as Error ?? personaCatalog.error as Error ?? personaProfilesQuery.error as Error;
    return <ErrorPanel title="Catalog Load Failed" message="Unable to load sizing catalog. Please try again." error={err} />;
  }

  // Model display name for cost simulator
  const modelDisplayName = costSnapshot?.modelId ?? sizingRequest?.model.id;
  return <div className="min-h-screen bg-background">
      {/* Introduction Section */}
      <IntroSection onScrollToSizing={scrollToSizing} onScrollToCost={scrollToCost} />
      
      {/* Sizing Section Divider */}
      <SectionDivider id="sizing-section" title="Infrastructure Sizing" icon={Server} />
      
      {/* Sizing Section */}
      <section ref={sizingSectionRef} className="py-8 scroll-mt-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Sizing Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-3xl font-bold text-foreground">
                  Plan Your On-Prem Inference Capacity
                </h2>
                <p className="text-muted-foreground">
                  Configure workload parameters and see infrastructure requirements
                </p>
              </div>
              <Button onClick={handleExportPdf} disabled={!snapshot || !sizingRequest || isExportingPdf} className="shrink-0 gap-2" variant="outline">
                {isExportingPdf ? <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating PDF...
                  </> : <>
                    <FileDown className="h-4 w-4" />
                    Export PDF Report
                  </>}
              </Button>
            </div>

            {/* Sizing Content */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 items-start">
              <div className="lg:col-span-1">
                <div className="sticky top-20 self-start">
                  <QuickControlsCollapsible tradeoffs={sizing.scenario.tradeoffs} users={sizing.scenario.users} totalPct={sizing.scenario.totalPct} warningUnderPct={sizing.scenario.warningUnderPct} blockedOverPct={sizing.scenario.blockedOverPct} catalog={catalog} catalogLoading={catalogLoading} selectedModelId={sizing.scenario.selectedModelId} onModelChange={sizing.setSelectedModelId} onTradeoffChange={sizing.setTradeoff} onUserUpdate={sizing.updateUser} onUserRemove={sizing.removeUser} onUserAdd={sizing.addUser} advanced={sizing.scenario.advanced} onAdvancedChange={sizing.setAdvanced} sticky={false} presets={SCENARIO_PRESETS} onPresetSelect={sizing.applyPreset} />
                </div>
              </div>

              <div className="lg:col-span-2 space-y-6">
                <SizingSummaryDashboard snapshot={snapshot} serverCount={snapshot ? Math.ceil(snapshot.finalGpuCount / 8) : 0} gpusPerServer={8} serverDisplayName={catalog?.server?.display_name ?? 'XE7740 Servers'} acceleratorDisplayName={catalog?.accelerator?.display_name ?? 'Intel Gaudi 3 Accelerators'} />
              </div>
            </div>

            {/* Full-width Advanced Insights */}
            {snapshot && sizingRequest && (
              <div className="mt-6">
                <NarrativePanels snapshot={snapshot} modelName={sizingRequest.model.id} serverSku="XE7740" serverDisplayName="Dell XE7740" gpusPerServer={8} hbmBytesPerGpu={(sizingRequest.model.hbmGiB ?? 96) * BYTES_PER_GIB} weightsGiB={sizingRequest.model.weightsGiB} weightsGiBUnshared={sizingRequest.model.weightsGiBUnshared ?? sizingRequest.model.weightsGiB} runtimeOverheadGiB={(sizingRequest.model.hbmGiB ?? 96) - sizingRequest.model.weightsGiB - sizingRequest.model.usableKvGiBPerGpu} usableKvGiBPerGpu={sizingRequest.model.usableKvGiBPerGpu} bridgeGroupSize={sizingRequest.model.bridgeGroupSize ?? 1} conversationTurns={sizingRequest.conversationTurns} latencyMs={sizingRequest.latencySloMs} batchSize={sizingRequest.batchSize} baseConcurrentUsers={sizingRequest.baseConcurrentUsers} personas={sizingRequest.personas} isComputing={loading} />
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Cost Simulator Section Divider */}
      <SectionDivider id="cost-section" title="Cost Analysis" icon={DollarSign} />
      
      {/* Cost Simulator Section */}
      <section ref={costSectionRef} className="py-8 pb-16 scroll-mt-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <CostSimulatorTool snapshot={costSnapshot} modelDisplayName={modelDisplayName} sizing={costSizing} />
          </div>
        </div>
      </section>
      
      {/* Sizing Config Drawer for Cost Simulator */}
      
      
      {/* Footer */}
      <footer className="border-t bg-muted/30 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            Powered by <span className="font-semibold text-foreground">VectorPath Consulting</span>
          </p>
        </div>
      </footer>

    </div>;
};
export default SizingCostCombined;
